import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LayoutDashboard, Settings, Clock, AlertCircle, CheckCircle2, XCircle, RefreshCw, TrendingUp, TrendingDown, Bell, Filter, Search, Eye, EyeOff, Edit, Save, Plus, Trash2, Info, HelpCircle, ExternalLink, Copy, Globe, Mail, Phone, MessageSquare, Heart, Award, Target, Timer, Brain, Route, Zap, MapPin, FileText, File, FileVideo, FileAudio, FileImage, FileSpreadsheet, FilePdf, FileZip, FileCheck, FileX, FilePlus, FileMinus, FileEdit, FileSearch, FileSlash, FileWarning, FileQuestion, FileHeart, FileClock, FileUp, FileDown, FileLeft, FileRight, FileBarChart, FilePieChart, FileLineChart, FileScissors, FileType, FileSymlink, FileStack, FileTree, FileBinary, FileCog, FileSettings, FileLock, FileUnlock, FileKey, FileSignature, FileVerified, FileBadge, FileTag, FileLabel, FileBookmark, FileStar, FileFlag, FileAlert, FileInfo, FileHelp, FileMessage, FileComment, FileChat, FileMail, FilePhone, Users, Activity, BarChart3, PieChart, LineChart, ScrollText, Database, HardDrive, Cpu, MemoryStick, Network, Server, Wrench, Shield, Archive, Download, Upload
} from 'lucide-react';
import { AdminService } from '../../services/AdminService';

const SystemMaintenance = () => {
  const [maintenanceMetrics, setMaintenanceMetrics] = useState(null);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [maintenanceConfig, setMaintenanceConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Create AdminService instance (memoized to avoid recreating on each render)
  const adminService = useMemo(() => new AdminService(), []);

  const loadMaintenanceData = useCallback(async () => {
    try {
      setLoading(true);
      const [metricsResponse, tasksResponse, logsResponse, configResponse] = await Promise.all([
        adminService.getMaintenanceMetrics(selectedTimeRange),
        adminService.getMaintenanceTasks(),
        adminService.getMaintenanceLogs(),
        adminService.getMaintenanceConfig()
      ]);

      if (metricsResponse.success) setMaintenanceMetrics(metricsResponse.data);
      if (tasksResponse.success) setMaintenanceTasks(tasksResponse.data);
      if (logsResponse.success) setMaintenanceLogs(logsResponse.data);
      if (configResponse.success) setMaintenanceConfig(configResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange]);

  useEffect(() => {
    loadMaintenanceData();
  }, [loadMaintenanceData]);

  const handleRunMaintenanceTask = async (taskId) => {
    try {
      const response = await adminService.runMaintenanceTask(taskId);
      if (response.success) {
        await loadMaintenanceData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleScheduleMaintenance = async (taskData) => {
    try {
      const response = await adminService.scheduleMaintenanceTask(taskData);
      if (response.success) {
        await loadMaintenanceData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateConfig = async (config) => {
    try {
      const response = await adminService.updateMaintenanceConfig(config);
      if (response.success) {
        setMaintenanceConfig(response.data);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredTasks = maintenanceTasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || task.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'running': return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'scheduled': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'pending': return <AlertCircle className="w-4 h-4 text-gray-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading maintenance data</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Maintenance</h1>
          <p className="text-gray-600">Manage system maintenance operations and monitoring</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button
            onClick={loadMaintenanceData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: LayoutDashboard },
            { id: 'tasks', name: 'Maintenance Tasks', icon: Settings },
            { id: 'logs', name: 'Maintenance Logs', icon: ScrollText },
            { id: 'config', name: 'Configuration', icon: Settings }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && maintenanceMetrics && (
        <div className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {maintenanceMetrics.overviewMetrics.map((metric, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <div className={`p-3 rounded-full ${
                    metric.status === 'healthy' ? 'bg-green-100' :
                    metric.status === 'warning' ? 'bg-yellow-100' :
                    'bg-red-100'
                  }`}>
                    {metric.status === 'healthy' ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : metric.status === 'warning' ? (
                      <AlertCircle className="w-6 h-6 text-yellow-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-600" />
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center">
                  {metric.trend === 'up' ? (
                    <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                  )}
                  <span className={`text-sm ${
                    metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.change}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* System Health Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">System Health Status</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {maintenanceMetrics.systemHealth.map((health, index) => (
                  <div key={index} className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                      health.status === 'healthy' ? 'bg-green-100' :
                      health.status === 'warning' ? 'bg-yellow-100' :
                      'bg-red-100'
                    }`}>
                      {health.status === 'healthy' ? (
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      ) : health.status === 'warning' ? (
                        <AlertCircle className="w-8 h-8 text-yellow-600" />
                      ) : (
                        <XCircle className="w-8 h-8 text-red-600" />
                      )}
                    </div>
                    <h4 className="text-lg font-medium text-gray-900">{health.component}</h4>
                    <p className="text-sm text-gray-600">{health.description}</p>
                    <p className={`text-sm font-medium mt-1 ${
                      health.status === 'healthy' ? 'text-green-600' :
                      health.status === 'warning' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {health.status}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Maintenance Activity</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {maintenanceMetrics.recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      {getStatusIcon(activity.status)}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{activity.taskName}</p>
                        <p className="text-sm text-gray-600">{activity.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">{activity.timestamp}</p>
                      <p className="text-sm text-gray-600">{activity.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search maintenance tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="failed">Failed</option>
              <option value="scheduled">Scheduled</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Maintenance Tasks List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Maintenance Tasks</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Run</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{task.name}</div>
                          <div className="text-sm text-gray-600">{task.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {getStatusIcon(task.status)}
                          <span className="ml-1">{task.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{task.schedule}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{task.lastRun}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{task.duration}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          {task.status === 'pending' && (
                            <button
                              onClick={() => handleRunMaintenanceTask(task.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Run
                            </button>
                          )}
                          <button className="text-gray-600 hover:text-gray-900">
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-6">
          {/* Logs List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Maintenance Logs</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {maintenanceLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.timestamp}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.taskName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                          {getStatusIcon(log.status)}
                          <span className="ml-1">{log.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.duration}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && maintenanceConfig && (
        <div className="space-y-6">
          {/* Configuration Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Maintenance Configuration</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Auto Maintenance</label>
                  <select
                    value={maintenanceConfig.autoMaintenance ? 'enabled' : 'disabled'}
                    onChange={(e) => setMaintenanceConfig({...maintenanceConfig, autoMaintenance: e.target.value === 'enabled'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Maintenance Window</label>
                  <input
                    type="text"
                    value={maintenanceConfig.maintenanceWindow}
                    onChange={(e) => setMaintenanceConfig({...maintenanceConfig, maintenanceWindow: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Log Retention (Days)</label>
                  <input
                    type="number"
                    value={maintenanceConfig.logRetentionDays}
                    onChange={(e) => setMaintenanceConfig({...maintenanceConfig, logRetentionDays: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notification Enabled</label>
                  <select
                    value={maintenanceConfig.notificationEnabled ? 'enabled' : 'disabled'}
                    onChange={(e) => setMaintenanceConfig({...maintenanceConfig, notificationEnabled: e.target.value === 'enabled'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => handleUpdateConfig(maintenanceConfig)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemMaintenance;